import { HybridTooltip, Icon } from '@advanza/ui'
import NiceModal from '@ebay/nice-modal-react'
import ManualReclaimModal from 'components/matches/ManualReclaimModal'
import ReclaimActionsMenu from 'components/reclaims/ReclaimActionsMenu'
import { ReclaimActionContext } from 'interfaces/Reclaims'
import { ReactNode } from 'react'

export const LEAD_TYPE_MATCHES = 'matches'
export const LEAD_TYPE_PHONE_CLICKS = 'phoneClicks'
export const LEAD_TYPE_WEBSITE_CLICKS = 'websiteClicks'

export type LeadType =
    | typeof LEAD_TYPE_MATCHES
    | typeof LEAD_TYPE_PHONE_CLICKS
    | typeof LEAD_TYPE_WEBSITE_CLICKS

interface Props {
    lead: any
    leadType: LeadType
    id: number
}

interface ReclaimDetailsTooltipProps {
    lead: any
    children: ReactNode
}

const ReclaimDetailsTooltip = ({ lead, children }: ReclaimDetailsTooltipProps) => {
    return (
        <HybridTooltip
            childrenContent={
                <div>
                    <div>
                        <b>Reason:</b> {lead.reclaimReason || 'no info'}
                    </div>
                    <div className="mt-1">
                        <b>Supplier Comment:</b> {lead.reclaimExtraField || 'no info'}
                    </div>
                    <div className="mt-1">
                        <b>Admin comment:</b> {lead.reclaimAdminComment || 'no info'}
                    </div>
                </div>
            }>
            <span>{children}</span>
        </HybridTooltip>
    )
}

/** Status word + colour for an existing reclaim on a matches/phone-clicks list row. */
const getReclaimStatus = (lead: any): { label: string; className: string } => {
    if (lead.reclaimDeleted) {
        return { label: 'Reclaim deleted', className: 'text-tertiary-800' }
    }
    if (lead.reclaimWithdrawn) {
        return { label: 'Reclaim withdrawn', className: 'text-primary-500' }
    }
    if (lead.reclaimDenied) {
        return { label: 'Reclaim rejected', className: 'text-tertiary-800' }
    }
    if (lead.reclaimAccepted || lead.reclaimed) {
        return { label: 'Reclaim accepted', className: 'text-secondary-900' }
    }
    return { label: 'Reclaim open', className: 'text-paused-orange' }
}

/** Map the flattened reclaim fields on a list row to the shape ReclaimActionsMenu expects. */
const leadToReclaim = (lead: any): ReclaimActionContext => {
    const accepted = lead.reclaimAccepted || lead.reclaimed || null
    const denied = lead.reclaimDenied || null
    const withdrawn = lead.reclaimWithdrawn || null
    const deleted = lead.reclaimDeleted || null

    return {
        reclaim_id: lead.reclaimId,
        accepted,
        denied,
        withdrawn,
        deleted,
        is_open: !accepted && !denied && !withdrawn && !deleted,
        is_pending_ai_processing: false,
        admin_comment: lead.reclaimAdminComment ?? null,
        reason: lead.reclaimReason ?? null,
        requestId: lead.requestId,
        firstName: lead.userName,
    }
}

const ManualReclaimButton = ({ lead, leadType, id }: Props) => {
    if (!lead.reclaimId) {
        return (
            <div className="flex items-center">
                <button
                    title="Create an approved reclaim."
                    onClick={() =>
                        NiceModal.show(ManualReclaimModal, { lead, leadType, leadId: id })
                    }>
                    <Icon name="replay" />
                </button>
                <HybridTooltip
                    childrenContent={
                        <div>
                            No reclaim created. Click on the arrow to create an approved reclaim.
                        </div>
                    }>
                    <div className="flex justify-center">
                        <Icon name="info" fontSize={14} className="align-middle ml-1" />
                    </div>
                </HybridTooltip>
            </div>
        )
    }

    const { label, className } = getReclaimStatus(lead)

    return (
        <div className="flex items-center gap-1">
            <ReclaimDetailsTooltip lead={lead}>
                <small className={className}>{label}</small>
            </ReclaimDetailsTooltip>
            <ReclaimActionsMenu reclaim={leadToReclaim(lead)} iconTrigger />
        </div>
    )
}

export default ManualReclaimButton
